import { observer } from 'mobx-react-lite'
import i from "../translations/i"
import { pathHelper } from '../helpers/path'

import {
    DashboardOutlined,
    ShopOutlined,
    ShoppingOutlined,
    TagsOutlined,
    PercentageOutlined,
    FilterOutlined,
    CreditCardOutlined,
    SnippetsOutlined,
    FundProjectionScreenOutlined,
    AppstoreOutlined,
    UserOutlined,
    PicRightOutlined,
    HighlightOutlined,
    ContactsOutlined,
    DeploymentUnitOutlined,
    SettingOutlined,
    EuroOutlined,
    NotificationOutlined,
    ExceptionOutlined,
    SolutionOutlined,
    StarOutlined,
    GroupOutlined, UnorderedListOutlined, FireOutlined, BranchesOutlined, DownOutlined, RightOutlined, ShoppingCartOutlined, WalletOutlined
} from '@ant-design/icons'

import './style/navigation.less'
import layoutController from './controllers/layout.controller'
import { Badge, Tooltip } from 'antd'
import { ReactNode, useRef, useState } from "react";
import { storeLocal } from "../helpers/stores";
import { isNewNavigationItem, useStickyNavigation } from './navigation.helpers'

// Пункт меню. newSince — дата появления ('YYYY-MM-DD'): месяц после неё рядом с пунктом плашка «Новое»,
// потом она пропадает сама, чистить код не нужно
interface NavigationItem {
    title: string
    url: string
    icon: ReactNode
    key: string
    badge: number
    active: boolean
    role: number[]
    newSince?: string
}

// Раздел меню. Без заголовка — пункты без шапки; раздел с url и без пунктов — сам ссылка (как «Настройки»)
interface NavigationGroup {
    key: string
    role: boolean
    title?: string
    icon?: ReactNode
    url?: string
    badge?: number
    active?: boolean
    newSince?: string
    items: NavigationItem[]
}

// Плашка «Новое» рядом с пунктом
const NewMark = () => <span className="navigation-new">{i.t('nav:newItem')}</span>

// Точка у заголовка свёрнутого раздела, в котором есть новые пункты
const NewGroupDot = () => (
    <Tooltip title={i.t('nav:newInGroup')} placement="right">
        <span className="navigation-new-dot" aria-label={i.t('nav:newInGroup')} />
    </Tooltip>
)

// sticky — меню рядом с контентом (десктоп): длинное листается вместе со страницей, см. useStickyNavigation
export const Navigation = observer(({ sticky = false }: { sticky?: boolean }) => {

    const this_path = pathHelper(window.location.pathname)

    const navigationRef = useRef<HTMLDivElement>(null)
    useStickyNavigation(navigationRef, sticky)

    const nav: NavigationGroup[] = [
        {
            key: "navigation_dashboard",
            role: true,
            items: [
                { title: i.t('nav:dashboard'), url: "/", icon: <DashboardOutlined />, key: "navigation_shop_dashboard", badge: 0, active: this_path === '/', role: [1] },
                { title: i.t('nav:apps'), url: "/apps", icon: <AppstoreOutlined />, key: "navigation_apps", badge: 0, active: this_path === '/apps', role: [] },
                { title: i.t('nav:billing'), url: "/billing", icon: <WalletOutlined />, key: "navigation_billing", badge: 0, active: this_path === '/billing', role: [], newSince: '2026-10-07' },
            ]
        },
        {
            key: "navigation_shop",
            role: true,
            title: i.t('nav:shop'),
            icon: <ShopOutlined />,
            items: [
                { title: i.t('nav:catalog'), url: "/shop/catalog", icon: <ShopOutlined />, key: "navigation_shop_catalog", badge: 0, active: this_path === '/shop/catalog', role: [4, 6] },
                { title: i.t('nav:products'), url: "/catalog/products", icon: <ShoppingOutlined />, key: "navigation_shop_products", badge: 0, active: this_path === '/catalog/products', role: [4, 6] },
                { title: i.t('nav:lists'), url: "/catalog/lists", icon: <UnorderedListOutlined />, key: "navigation_shop_groups", badge: 0, active: this_path === '/catalog/lists', role: [6] },
                { title: i.t('nav:groups'), url: "/catalog/groups", icon: <GroupOutlined />, key: "navigation_shop_lists", badge: 0, active: this_path === '/catalog/groups', role: [] },
                { title: i.t('nav:productsStatuses'), url: "/shop/status", icon: <TagsOutlined /> , key: "navigation_shop_products_statuses", badge: 0, active: this_path === '/shop/status', role: [6]},
                { title: i.t('nav:filters'), url: "/shop/properties", icon: <FilterOutlined />, key: "navigation_shop_filters", badge: 0, active: this_path === '/shop/properties', role: [6] },
                { title: i.t('nav:units'), url: "/shop/units", icon: <DeploymentUnitOutlined />, key: "navigation_shop_units", badge: 0, active: this_path === '/shop/units', role: [6] },
                { title: i.t('nav:modifiers_groups'), url: "/shop/modifiers_groups", icon: <BranchesOutlined />, key: "navigation_shop_modifiers_gorups", badge: 0, active: this_path === '/shop/modifiers_groups', role: [6] },
                { title: i.t('nav:modifiers'), url: "/shop/modifiers", icon: <BranchesOutlined />, key: "navigation_shop_modifiers", badge: 0, active: this_path === '/shop/modifiers', role: [6] },
            ]
        },
        {
            key: "navigation_sale",
            role: true,
            title: i.t('nav:sale'),
            icon: <CreditCardOutlined />,
            items: [
                { title: i.t('nav:orders'), url: "/shop/orders", icon: <CreditCardOutlined />, key: "navigation_shop_orders", badge: layoutController.markers.orders ? layoutController.markers.orders : 0, active: this_path === '/shop/orders', role: [1, 6] },
                { title: i.t('nav:ordersStatuses'), url: "/shop/statuses", icon: <SnippetsOutlined />, key: "navigation_shop_orders_statuses", badge: 0, active: this_path === '/shop/statuses', role: [] },
                { title: i.t('nav:users'), url: "/users", icon: <UserOutlined />, key: "navigation_other_users", badge: 0, active: this_path === '/users', role: [1] },
                { title: i.t('nav:carts'), url: "/carts", icon: <ShoppingCartOutlined />, key: "navigation_other_carts", badge: 0, active: this_path === '/carts' || this_path.startsWith('/carts/'), role: [], newSince: '2026-10-19' },
            ]
        },
        {
            key: "navigation_marketing",
            role: true,
            title: i.t('nav:marketing'),
            icon: <PercentageOutlined />,
            items: [
                { title: i.t('nav:screens'), url: "/screens", icon: <FundProjectionScreenOutlined />, key: "navigation_other_screens", badge: 0, active: this_path === '/screens', role: [6] },
                { title: i.t('def:Promo Slides'), url: "/promo_slides", icon: <FireOutlined />, key: "navigation_other_promo_slides", badge: 0, active: this_path === '/promo_slides', role: [] },
                { title: i.t('def:Coupons'), url: "/shop/coupons", icon: <PercentageOutlined />, key: "navigation_shop_promocodes", badge: 0, active: this_path === '/shop/coupons', role: [] },
                { title: i.t('nav:pushes'), url: "/pushes", icon: <NotificationOutlined />, key: "navigation_pushes", badge: 0, active: this_path === '/pushes', role: [] },
                { title: i.t('nav:discountsAndGifts'), url: "/settings/discount", icon: <EuroOutlined />, key: "navigation_shop_payment_types", badge: 0, active: this_path === '/settings/discount', role: [] },
            ]
        },
        {
            key: "navigation_news",
            role: true,
            title: i.t('def:News'),
            icon: <HighlightOutlined />,
            items: [
                { title: i.t('nav:news'), url: "/news", icon: <HighlightOutlined />, key: "navigation_news_news", badge: 0, active: this_path === '/news', role: [6] },
                { title: i.t('def:News categories'), url: "/news_categories", icon: <PicRightOutlined />, key: "navigation_news_categories", badge: 0, active: this_path === '/news_categories', role: [] },
                { title: i.t('nav:newTypes'), url: "/news_types", icon: <PicRightOutlined />, key: "navigation_news_types", badge: 0, active: this_path === '/news_types', role: [] },
                { title: i.t('def:News props'), url: "/news_props", icon: <PicRightOutlined />, key: "navigation_news_props", badge: 0, active: this_path === '/news_props', role: [] },
            ]
        },
        {
            key: "navigation_feedback",
            role: true,
            title: i.t('nav:feedbackSection'),
            icon: <ContactsOutlined />,
            items: [
                { title: i.t('nav:contacts'), url: "/contacts", icon: <ContactsOutlined />, key: "navigation_contacts_contacts", badge: 0, active: this_path === '/contacts', role: [6] },
                { title: i.t('nav:feedback'), url: '/shop/feedback', icon: <SolutionOutlined />, key: 'navigation_other_feedback', badge: layoutController.markers.feedback ? layoutController.markers.feedback : 0, active: this_path === '/shop/feedback', role: [] },
                { title: i.t('nav:forms'), url: '/shop/forms', icon: <ExceptionOutlined />, key: 'navigation_other_forms', badge: layoutController.markers.forms ? layoutController.markers.forms : 0, active: this_path === '/shop/forms', role: [] },
                { title: i.t('nav:reviews'), url: '/shop/reviews', icon: <StarOutlined />, key: 'navigation_other_reviews', badge: layoutController.markers.reviews ? layoutController.markers.reviews : 0, active: this_path === '/shop/reviews', role: [], newSince: '2026-09-21' },
            ]
        },
        {
            title: i.t('nav:settings'),
            url: "/settings",
            icon: <SettingOutlined />,
            key: "navigation_shop_settings",
            badge: 0,
            active: this_path === '/settings',
            role: true,
            items: []
        },

    ]

    const activeGroupKey = nav.find(group =>
        group.title && group.items.some(el => el.active)
    )?.key

    const [openedGroupKey, setOpenedGroupKey] = useState<string | null>(activeGroupKey ?? null)

    const isGroupCollapsible = (item: NavigationGroup) => !!item.title

    // const isGroupOpen = (item: NavigationGroup) => {
    //     if (!isGroupCollapsible(item)) return true
    //
    //     return openedGroupKey === item.key
    // }
    const isGroupOpen = (item: NavigationGroup) => true

    const toggleGroup = (key: string) => {
        setOpenedGroupKey(openedGroupKey === key ? null : key)
    }

    const canShowNavigationItem = (el: NavigationItem) => {
        const role = storeLocal.get('role')

        return !role
            || role === 0
            || role === 5
            || el.role.findIndex((r: number) => r === Number(role)) !== -1
    }

    const getGroupBadge = (item: NavigationGroup) => {
        return item.items.reduce((sum: number, el) => {
            if (!canShowNavigationItem(el)) {
                return sum
            }

            return sum + Number(el.badge || 0)
        }, 0)
    }

    // В свёрнутом разделе новые пункты не видно — тогда сам раздел помечается точкой
    const hasNewItems = (item: NavigationGroup) =>
        item.items.some(el => canShowNavigationItem(el) && isNewNavigationItem(el.newSince))

    return(
        <>
            <div className="navigation-container">
                <div className="navigation" ref={navigationRef}>
                    <div className="navigation-list">
                        {nav.map((item, i: number) => !storeLocal.get('role') || storeLocal.get('role') === 0 || storeLocal.get('role') === 5 || item.items.findIndex(_item => _item.role.filter((_filter: number) => _filter === storeLocal.get('role')).length > 0) > -1 ? (
                            <div key={`navigation_elements_${i}`} className="navigation-rel">
                                {(item.title || item.title === "") &&
                                    (() => {
                                        const TitleTag: any = item.url ? 'a' : 'div'

                                        return (
                                            <TitleTag
                                                href={item.url}
                                                className={
                                                    (isGroupCollapsible(item) ? 'navigation-title navigation-title-clickable' : 'navigation-title')
                                                    + (item.url && item.active ? ' active' : '')
                                                }
                                                // onClick={() => isGroupCollapsible(item) && toggleGroup(item.key)}
                                            >
                                                {item.title !== "" &&
                                                    <div className="navigation-title-content">
                                                        <span className="navigation-title-left">
                                                            {item.icon &&
                                                                <span className="navigation-title-icon">{item.icon}</span>
                                                            }

                                                                <Badge
                                                                    count={getGroupBadge(item)}
                                                                    size="small"
                                                                    style={{ backgroundColor: '#52c41a' }}
                                                                    offset={[10, 0]}
                                                                >
                                                                <span className="titles">{item.title}</span>
                                                            </Badge>

                                                            {isNewNavigationItem(item.newSince) && <NewMark />}
                                                        </span>

                                                        {!isGroupOpen(item) && hasNewItems(item) && <NewGroupDot />}

                                                        {/*<span className="navigation-title-arrow">*/}
                                                        {/*    {isGroupOpen(item) ? <DownOutlined /> : <RightOutlined />}*/}
                                                        {/*</span>*/}
                                                    </div>
                                                }
                                            </TitleTag>
                                        )
                                    })()
                                }

                                {isGroupOpen(item) &&
                                    <div className="navigation-items">
                                        {item.items.map((el, index: number) => {
                                            if (!canShowNavigationItem(el)) {
                                                return null
                                            }

                                            return (
                                                <div key={el.key} className="navigation-item">
                                                    <a href={el.url} className={el.active ? 'navigation-link active' : 'navigation-link'}>
                                                        <Badge
                                                            count={el.badge ? el.badge : 0}
                                                            size="small"
                                                            style={{ backgroundColor: '#52c41a' }}
                                                            offset={[10, 10]}
                                                        >
                                                            <div
                                                                key={`navigation_elements_parent_${i}_element_${index}`}
                                                                className={el.active ? 'navigation_element navigation_active' : 'navigation_element'}
                                                            >
                                                                <div className="inline">
                                                                    <div className="icon">{el.icon}</div>
                                                                    <div className="point">{el.title}</div>
                                                                    {isNewNavigationItem(el.newSince) && <NewMark />}
                                                                </div>
                                                            </div>
                                                        </Badge>
                                                    </a>
                                                </div>
                                            )
                                        })}
                                    </div>
                                }
                            </div>
                        ) : null)}
                    </div>
                </div>
            </div>
        </>
    )
})